import { applyToPoint, invert, type Matrix, type NodeRecord, type Rect } from '@flobi/artwork-core';
import type { Point, ToolContext } from './types';

/**
 * A repeat's handles on the canvas — direction C's, added to pick A (Tarek, 2026-10-01:
 * "add the canvas handles from C too"; https://claude.ai/artifact/XHBMutSiz1FuEDxxrAZP1s).
 *
 * Round handles with an arrow in them, just outside the repeat's frame, saying which way to
 * pull: on a grid, → for more across, ↓ for more down and the corner ↘ to spread them; on a
 * row, → for more copies and the corner ↔ to spread them; on a circle, ↔ for its size. They
 * set the same numbers the Repeat popover names (Across, Down, Gap, Copies, Size), and their
 * label says it in those words. A path repeat has none: the path decides.
 *
 * Copies are laid out CENTRED on the repeat (the engine's `placements`), so adding a column
 * would grow both sides at once and the far side would run away from the hand. Every handle
 * that adds or spreads therefore moves the repeat by half of what it adds, and the side
 * across from the hand stays where it was — the way a drawer is pulled, not inflated.
 *
 * The cloner tool's single "Spacing" knob on the last copy did the job of the corner alone,
 * with nothing to say it was there; it is these handles now, under both tools.
 */

export type RepeatGrip = 'across' | 'down' | 'spread' | 'copies' | 'size';

export interface RepeatHandle {
  readonly grip: RepeatGrip;
  /** Where it sits on the page. */
  readonly at: Point;
  /** Which way pulling it adds, on the page: what its arrow points along. */
  readonly toward: Point;
  /** An arrow both ways: it can go either way and nothing is added, only spread. */
  readonly both: boolean;
}

/** How far outside the frame a handle sits, in screen pixels: clear of the corners' turn ring. */
const STANDOFF = 24;
/** How near a handle counts as on it, in screen pixels. Its disc is 18 across. */
export const GRIP_REACH = 11;
/** The numbers the popover allows, so a long pull stops where typing would. */
const MOST_ACROSS = 200;
const MOST_COPIES = 500;

type Cloner = Extract<NodeRecord, { type: 'cloner' }>;

const unit = (x: number, y: number): Point => {
  const length = Math.hypot(x, y) || 1;
  return { x: x / length, y: y / length };
};

/** Which way a local direction points on the page, as a unit. */
const onPage = (world: Matrix, x: number, y: number): Point => unit(world[0] * x + world[2] * y, world[1] * x + world[3] * y);

const away = (from: Point, toward: Point, distance: number): Point => ({ x: from.x + toward.x * distance, y: from.y + toward.y * distance });

/**
 * The handles of one repeat, around its frame — the box the selection draws, in the
 * repeat's own space (`box`) and the matrix that puts it on the page (`world`).
 */
export function repeatHandles(ctx: Pick<ToolContext, 'node' | 'unitsPerPixel'>, id: string, box: Rect, world: Matrix): RepeatHandle[] {
  const node = ctx.node(id);
  if (node?.type !== 'cloner' || node.mode === 'path') return [];
  const out = STANDOFF * ctx.unitsPerPixel;
  const at = (x: number, y: number) => applyToPoint(world, x, y);
  const right = box.x + box.width;
  const bottom = box.y + box.height;
  const middle = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
  const across = onPage(world, 1, 0);
  const down = onPage(world, 0, 1);
  // The corner's handle stands off along the diagonal, far enough that it is past the
  // turn ring round the corner and not just beside it.
  const corner = (dx: number, dy: number, both: boolean, toward?: Point): RepeatHandle => {
    const diagonal = onPage(world, dx, dy);
    return { grip: 'spread', at: away(at(dx > 0 ? right : box.x, dy > 0 ? bottom : box.y), diagonal, out * 1.25), toward: toward ?? diagonal, both };
  };

  if (node.mode === 'grid') {
    const handles: RepeatHandle[] = [
      { grip: 'across', at: away(at(right, middle.y), across, out), toward: across, both: false },
      { grip: 'down', at: away(at(middle.x, bottom), down, out), toward: down, both: false },
    ];
    // Nothing to spread with one copy.
    if (Math.floor(node.countX) > 1 || Math.floor(node.countY) > 1) handles.push(corner(1, 1, false));
    return handles;
  }

  if (node.mode === 'linear') {
    // A run whose numbers are its whole length (End Point) keeps its length whatever the
    // count, so pulling its end could add nothing; it is set on the Cloner strip.
    if (node.endPoint) return [];
    const run = unit(node.stepX, node.stepY);
    if (node.stepX === 0 && node.stepY === 0) return [];
    // Where a line from the middle along the run leaves the frame: the end of the run.
    const reach = Math.min(run.x === 0 ? Infinity : box.width / 2 / Math.abs(run.x), run.y === 0 ? Infinity : box.height / 2 / Math.abs(run.y));
    const end = at(middle.x + run.x * reach, middle.y + run.y * reach);
    const along = onPage(world, run.x, run.y);
    const handles: RepeatHandle[] = [{ grip: 'copies', at: away(end, along, out), toward: along, both: false }];
    if (Math.floor(node.count) > 1) handles.push(corner(run.x >= 0 ? 1 : -1, run.y >= 0 ? 1 : -1, true, along));
    return handles;
  }

  // A circle: one handle out to the side, for how big it is.
  return [{ grip: 'size', at: away(at(right, middle.y), across, out), toward: across, both: true }];
}

/** The handle under a point, if any. */
export function repeatHandleAt(ctx: Pick<ToolContext, 'unitsPerPixel'>, handles: readonly RepeatHandle[], world: Point): RepeatHandle | null {
  const reach = GRIP_REACH * ctx.unitsPerPixel;
  let best: RepeatHandle | null = null;
  let nearest = reach;
  for (const handle of handles) {
    const distance = Math.hypot(handle.at.x - world.x, handle.at.y - world.y);
    if (distance <= nearest) {
      nearest = distance;
      best = handle;
    }
  }
  return best;
}

/** The cursor a handle wears: a resize along the way its arrow points. */
export function gripCursor(handle: RepeatHandle): string {
  const degrees = ((Math.atan2(handle.toward.y, handle.toward.x) * 180) / Math.PI + 180) % 180;
  if (degrees < 22.5 || degrees >= 157.5) return 'ew-resize';
  if (degrees < 67.5) return 'nwse-resize';
  if (degrees < 112.5) return 'ns-resize';
  return 'nesw-resize';
}

/** What a pull on a handle started from: enough to work out every step of it, and to put it back. */
export interface RepeatPull {
  readonly id: string;
  readonly grip: RepeatGrip;
  readonly mode: Cloner['mode'];
  readonly from: Point;
  /** Page to the repeat's own space, as the pull began. */
  readonly back: Matrix;
  readonly was: { countX: number; countY: number; count: number; stepX: number; stepY: number; radius: number; transform: Matrix };
}

export function startPull(ctx: Pick<ToolContext, 'node' | 'scene'>, id: string, grip: RepeatGrip, from: Point): RepeatPull | null {
  const node = ctx.node(id);
  const back = invert(ctx.scene.worldTransform(id));
  if (node?.type !== 'cloner' || !back) return null;
  return {
    id,
    grip,
    mode: node.mode,
    from,
    back,
    was: { countX: node.countX, countY: node.countY, count: node.count, stepX: node.stepX, stepY: node.stepY, radius: node.radius, transform: [...node.transform] as Matrix },
  };
}

const clamp = (value: number, least: number, most: number) => Math.max(least, Math.min(most, value));

/** The repeat moved by a vector in its own space, as a transform in its parent's. */
const shifted = (transform: Matrix, x: number, y: number): Matrix => [
  transform[0],
  transform[1],
  transform[2],
  transform[3],
  transform[4] + transform[0] * x + transform[2] * y,
  transform[5] + transform[1] * x + transform[3] * y,
];

/**
 * What the repeat becomes with the pointer here: the props to write, or null for nothing.
 *
 * Read in the repeat's own space as the pull began, so a turned or flipped repeat is pulled
 * along its own axes. The edge being pulled lands under the hand: a column is a whole step
 * of travel, because the side across stays put (`shifted` by half of what is added).
 */
export function pulled(pull: RepeatPull, world: Point): Record<string, unknown> | null {
  const start = applyToPoint(pull.back, pull.from.x, pull.from.y);
  const now = applyToPoint(pull.back, world.x, world.y);
  const dx = now.x - start.x;
  const dy = now.y - start.y;
  const { was } = pull;

  if (pull.grip === 'across' || pull.grip === 'down') {
    const sideways = pull.grip === 'across';
    const step = Math.abs(sideways ? was.stepX : was.stepY);
    if (step < 1) return null;
    const had = Math.max(1, Math.floor(sideways ? was.countX : was.countY));
    const count = clamp(had + Math.round((sideways ? dx : dy) / step), 1, MOST_ACROSS);
    const half = ((count - had) * step) / 2;
    return sideways
      ? { countX: count, transform: shifted(was.transform, half, 0) }
      : { countY: count, transform: shifted(was.transform, 0, half) };
  }

  if (pull.grip === 'copies') {
    const length = Math.hypot(was.stepX, was.stepY);
    if (length < 1) return null;
    const had = Math.max(1, Math.floor(was.count));
    const count = clamp(had + Math.round((dx * was.stepX + dy * was.stepY) / length / length), 1, MOST_COPIES);
    const share = (count - had) / 2;
    return { count, transform: shifted(was.transform, was.stepX * share, was.stepY * share) };
  }

  if (pull.grip === 'size') return { radius: Math.max(1, Math.round(was.radius + dx)) };

  // Spread. The corner moves as many gaps as there are spaces between copies, so the pull
  // is shared out over them; the first copy stays where it is.
  if (pull.mode === 'linear') {
    const spaces = Math.max(1, Math.floor(was.count)) - 1;
    const length = Math.hypot(was.stepX, was.stepY);
    if (spaces < 1 || length < 1) return null;
    const longer = Math.max(1, length + (dx * was.stepX + dy * was.stepY) / length / spaces) / length;
    const stepX = Math.round(was.stepX * longer);
    const stepY = Math.round(was.stepY * longer);
    return { stepX, stepY, transform: shifted(was.transform, ((stepX - was.stepX) * spaces) / 2, ((stepY - was.stepY) * spaces) / 2) };
  }
  // A grid: one gap, the same both ways, as the popover's Gap is.
  const spacesX = Math.max(1, Math.floor(was.countX)) - 1;
  const spacesY = Math.max(1, Math.floor(was.countY)) - 1;
  const shares = spacesX * spacesX + spacesY * spacesY;
  if (shares === 0) return null;
  const gap = (dx * spacesX + dy * spacesY) / shares;
  const sign = (value: number) => (value < 0 ? -1 : 1);
  const wideX = Math.round(Math.max(1, Math.abs(was.stepX) + gap));
  const wideY = Math.round(Math.max(1, Math.abs(was.stepY) + gap));
  return {
    stepX: sign(was.stepX) * wideX,
    stepY: sign(was.stepY) * wideY,
    transform: shifted(was.transform, ((wideX - Math.abs(was.stepX)) * spacesX) / 2, ((wideY - Math.abs(was.stepY)) * spacesY) / 2),
  };
}

/**
 * What a handle's label says, in the popover's own words: the number it sets, as it is now.
 * `source` is the size of what is repeated, which the Gap is measured from.
 */
export function gripReadout(node: NodeRecord | undefined, grip: RepeatGrip, source: { width: number; height: number } | null): string {
  if (node?.type !== 'cloner') return '';
  if (grip === 'across') return `Across ${Math.floor(node.countX)}`;
  if (grip === 'down') return `Down ${Math.floor(node.countY)}`;
  if (grip === 'copies') return `Copies ${Math.floor(node.count)}`;
  if (grip === 'size') return `Size ${Math.round(node.radius)}`;
  return `Gap ${Math.round(node.stepX - (source?.width ?? 0))}`;
}
